import React from 'react';
import { Input, Select, SelectItem, Button } from '@heroui/react';
import THEME from '../theme/constants';

const ExamTable = ({ 
  exams, 
  courseSearch, 
  setCourseSearch,
  buildingSearch,
  setBuildingSearch,
  roomSearch,
  setRoomSearch,
  filterDate, 
  setFilterDate, 
  showOnlyAvailable, 
  setShowOnlyAvailable,
  currentPage,
  totalPages,
  onPageChange,
  allExams // Add this new prop for all exams
}) => {
  // Generate a list of unique dates from ALL exams, not just filtered ones
  const uniqueDates = [...new Set(allExams.map(exam => exam.date))].sort();
  
  // First add this function near the top of your component:
  const formatDateOption = (date) => {
    const examDate = new Date(date);
    const today = new Date();
    
    // Check if this date is today
    if (examDate.getDate() === today.getDate() && 
        examDate.getMonth() === today.getMonth() &&
        examDate.getFullYear() === today.getFullYear()) {
      return "Today";
    }
    
    return date;
  };

  return (
    <div className="mt-8">
      <div className="mb-4 flex flex-wrap gap-3">
        {/* Separate search fields */}
        <div className="flex-1 min-w-[200px]">
          <Input
            variant='bordered'
            classNames={THEME.components.input.classNames}
            id="courseSearch"
            label="Course"
            placeholder="Search by course..."
            value={courseSearch}
            onChange={(e) => setCourseSearch(e.target.value)}
            fullWidth
          />
        </div>
        
        <div className="flex-1 min-w-[150px]">
          <Input
            variant='bordered'
            classNames={THEME.components.input.classNames}
            id="buildingSearch"
            label="Building"
            placeholder="Building number..."
            value={buildingSearch}
            onChange={(e) => setBuildingSearch(e.target.value)}
            fullWidth
          />
        </div>
        
        <div className="flex-1 min-w-[150px]">
          <Input
            variant='bordered'
            classNames={THEME.components.input.classNames}
            id="roomSearch"
            label="Room"
            placeholder="Room number..."
            value={roomSearch}
            onChange={(e) => setRoomSearch(e.target.value)}
            fullWidth
          />
        </div>
        
        <div className="flex-1 min-w-[180px]">
          <Select
            variant='bordered'
            classNames={{
              trigger: THEME.components.select.classNames.trigger,
              base: THEME.components.select.classNames.base,
              value: THEME.components.select.classNames.value,
              listbox: THEME.components.select.classNames.listbox,
              popoverContent: THEME.components.select.classNames.popoverContent
            }}
            id="dateFilter"
            label="Date"
            placeholder="All Dates"
            selectedKeys={filterDate ? [filterDate] : []}
            onSelectionChange={(keys) => {
              const selected = keys.size > 0 ? Array.from(keys)[0] : "";
              setFilterDate(selected);
            }}
            fullWidth
          >
            <SelectItem key="" value="">All Dates</SelectItem>
            {uniqueDates.map(date => (
              <SelectItem key={date} value={date}>
                {formatDateOption(date)}
              </SelectItem>
            ))}
          </Select>
        </div>
      </div>
      
      {exams.length > 0 ? (
        <>
          <div className="overflow-x-auto mt-2 rounded-lg border border-gray-100 dark:border-gray-800">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-10">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Building
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Room
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Section
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Time
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Course
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-950 divide-y divide-gray-200 dark:divide-gray-800">
                {exams.map((exam, index) => (
                  <tr key={index} className="odd:bg-white even:bg-gray-50 dark:odd:bg-gray-950 dark:even:bg-gray-900 hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {exam.building}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {exam.room}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {exam.section || ''}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {exam.date}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {exam.time}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {exam.course}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination controls */}
          <div className="mt-4 flex justify-center">
            <Button 
              onPress={() => onPageChange(currentPage - 1)} 
              disabled={currentPage === 1}
              variant="outlined"
              color="secondary"
              className="bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Previous
            </Button>
            
            <span className="px-3 py-1 mx-1 text-gray-700 dark:text-gray-300">
              Page {currentPage} of {totalPages || 1}
            </span>
            
            <Button 
              onPress={() => onPageChange(currentPage + 1)} 
              disabled={currentPage === totalPages || totalPages === 0}
              variant="outlined"
              className="bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              
            >
              Next
            </Button>
          </div>
        </>
      ) : (
  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No exams found matching your search criteria.
        </div>
      )}
    </div>
  );
};

export default ExamTable;